import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useT, useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, CheckCircle2, Clock, User } from "lucide-react";

export default function CollabPackageSlots() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { locale } = useI18n();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [availCache, setAvailCache] = useState({});

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try { const { data } = await api.get(`/collab-offerings/bookings/${bookingId}`); setBooking(data); }
    catch { setBooking(null); }
    finally { setLoading(false); }
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);

  const coachesById = useMemo(() => {
    const m = {};
    for (const c of (booking?.offering?.coaches || [])) m[c.id] = c;
    return m;
  }, [booking]);

  const pending = booking?.pending_one_on_one_blocks || [];
  const allDone = pending.length === 0;

  const fetchAvail = async (block_id, coach_id) => {
    const key = `${block_id}::${coach_id}`;
    if (availCache[key]) return;
    setAvailCache((c) => ({ ...c, [key]: { loading: true, days: [] } }));
    try {
      const { data } = await api.get(`/coach/${coach_id}/availability/v2/resolved`);
      setAvailCache((c) => ({ ...c, [key]: { loading: false, days: data?.resolved || [] } }));
    } catch { setAvailCache((c) => ({ ...c, [key]: { loading: false, days: [] } })); }
  };

  const pickCoach = (block_id, coach_id) => {
    setDrafts((d) => ({ ...d, [block_id]: { coach_id, slot_datetime: undefined } }));
    fetchAvail(block_id, coach_id);
  };
  const pickSlot = (block_id, slot_datetime) => setDrafts((d) => ({ ...d, [block_id]: { ...(d[block_id] || {}), slot_datetime } }));

  const saveBlock = async (block_id) => {
    const draft = drafts[block_id];
    if (!draft?.coach_id || !draft?.slot_datetime) return;
    setSaving(block_id);
    try {
      await api.patch(`/collab-offerings/bookings/${bookingId}/slots`, { selections: [{ block_id, coach_id: draft.coach_id, slot_datetime: draft.slot_datetime }] });
      setDrafts((d) => { const nd = { ...d }; delete nd[block_id]; return nd; });
      await load();
      toast.success(t("cov.slot_save"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(null); }
  };

  if (loading) return <Spinner label={t("common.loading")} />;
  if (!booking) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate("/app/bookings")} data-testid="cov-slots-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /> {t("bkc.bookings_title")}</button>
        <p className="text-sm text-muted-foreground">{t("cov.not_found")}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="collab-package-slots">
      <div>
        <h1 className="font-display text-3xl text-foreground">{t("cov.slot_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("cov.slot_sub")}</p>
      </div>

      <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm" data-testid="cov-slots-progress">
        {allDone ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Clock className="h-4 w-4 text-amber-500" />}
        <span className="text-foreground">{allDone ? t("cov.slot_all_done") : t("cov.slot_pending", { n: pending.length })}</span>
      </div>

      {pending.map((b) => {
        const draft = drafts[b.block_id] || {};
        const singleCoach = (b.executing_coach_ids || []).length === 1;
        const coachId = draft.coach_id || (singleCoach ? b.executing_coach_ids[0] : undefined);
        if (singleCoach && !draft.coach_id && coachId) { fetchAvail(b.block_id, coachId); }
        const key = coachId ? `${b.block_id}::${coachId}` : "";
        const avail = key ? availCache[key] : undefined;
        const days = (avail?.days || []).filter((d) => d.available && (d.slots || []).length > 0).slice(0, 14);
        return (
          <div key={b.block_id} className="rounded-2xl border border-border bg-card p-4" data-testid={`cov-slot-block-${b.block_id}`}>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <p className="flex-1 font-medium text-foreground">{b.title}</p>
              <span className="text-xs text-muted-foreground">{b.duration_min} {t("cov.min")}</span>
            </div>

            {(b.executing_coach_ids || []).length > 1 ? (
              <div className="mt-3">
                <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">{t("cov.slot_pick_coach")}</p>
                <div className="flex flex-wrap gap-2">
                  {b.executing_coach_ids.map((cid) => {
                    const on = draft.coach_id === cid;
                    return (
                      <button key={cid} data-testid={`cov-slot-block-${b.block_id}-coach-${cid}`} onClick={() => pickCoach(b.block_id, cid)}
                        className={`rounded-full px-3 py-1.5 text-sm transition-colors ${on ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground hover:bg-accent/70"}`}>
                        {coachesById[cid]?.name || "?"}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-foreground"><User className="h-4 w-4 text-primary" /> {coachesById[coachId]?.name || "?"}</p>
            )}

            {coachId ? (
              <div className="mt-3">
                <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">{t("cov.slot_pick_slot")}</p>
                {!avail || avail.loading ? (
                  <Spinner className="min-h-[60px]" />
                ) : days.length === 0 ? (
                  <p className="text-sm italic text-muted-foreground">{t("cov.slot_none")}</p>
                ) : (
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {days.map((d) => (
                      <div key={d.date} className="shrink-0" data-testid={`cov-slot-block-${b.block_id}-day-${d.date}`}>
                        <p className="mb-1 text-center text-[11px] text-muted-foreground">{new Date(d.date).toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" })}</p>
                        <div className="flex flex-col gap-1">
                          {d.slots.slice(0, 8).map((sl) => {
                            const iso = `${d.date}T${sl.start}`;
                            const on = draft.slot_datetime === iso;
                            return (
                              <button key={`${d.date}-${sl.start}`} data-testid={`cov-slot-block-${b.block_id}-slot-${d.date}-${sl.start}`} onClick={() => pickSlot(b.block_id, iso)}
                                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${on ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground hover:bg-accent/70"}`}>
                                {sl.start}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {draft.slot_datetime ? (
                  <Button className="mt-3 rounded-full" size="sm" disabled={saving === b.block_id} onClick={() => saveBlock(b.block_id)} data-testid={`cov-slot-block-${b.block_id}-save`}>
                    {saving === b.block_id ? t("common.saving") : t("cov.slot_save")}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {(booking.one_on_one_selections || []).length > 0 ? (
        <div>
          <p className="mb-2 text-sm font-medium text-foreground">{t("cov.slot_saved")}</p>
          <div className="space-y-2">
            {booking.one_on_one_selections.map((sel) => {
              const b = (booking.offering?.blocks || []).find((x) => x.id === sel.block_id);
              const dt = new Date(sel.slot_datetime);
              return (
                <div key={sel.block_id} className="flex items-center gap-2 rounded-xl border border-border bg-card p-3" data-testid={`cov-slot-saved-${sel.block_id}`}>
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{b?.title || "1:1"}</p>
                    <p className="text-xs text-muted-foreground">{coachesById[sel.coach_id]?.name || "?"} · {dt.toLocaleString(locale, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1 rounded-full" onClick={() => navigate(`/app/collab-package-booked/${bookingId}`)} data-testid="cov-slots-skip">{t("cov.slot_skip")}</Button>
        <Button className="flex-1 rounded-full" disabled={!allDone} onClick={() => navigate(`/app/collab-package-booked/${bookingId}`)} data-testid="cov-slots-done">{t("cov.slot_done")}</Button>
      </div>
    </div>
  );
}
